import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Statistiques d'un vendeur de marketplace
//
// Le vendeur suit l'activité de sa vitrine sur la marketplace :
//   • visites de sa vitrine (table visiteboutique)
//   • vues de ses annonces / produits / services
//   • commandes reçues et revenus
//   • clics / contacts WhatsApp sur ses annonces
//
// Les statistiques sont calculées sur une PÉRIODE (7 jours, 30 jours
// ou le mois en cours) et comparées à la période précédente équivalente.
// Toutes les requêtes sont défensives : une table vide ou une colonne
// manquante ne doit jamais casser l'espace vendeur.
// ─────────────────────────────────────────────────────────────

/** Période d'analyse des statistiques du vendeur. */
export type StatsPeriod = '7d' | '30d' | 'month';

export interface PeriodRange {
  from: Date;
  to: Date;
  prevFrom: Date;
  prevTo: Date;
}

/** Bloc de statistique : valeur sur la période + période précédente. */
export interface StatBlock {
  value: number;
  previous: number;
}

export interface VendorStats {
  /** Visites de la vitrine du vendeur. */
  shopVisits: StatBlock;
  /** Vues de ses annonces (produits + services). */
  contentViews: StatBlock;
  /** Commandes reçues. */
  orders: StatBlock;
  /** Revenus sur la période. */
  revenue: StatBlock;
  /** Clics / prises de contact sur ses annonces (cumul, hors période). */
  clicks: number;
  /** Nombre d'annonces publiées (cumul, hors période). */
  annonces: number;
}

const EMPTY_BLOCK: StatBlock = { value: 0, previous: 0 };

export const EMPTY_VENDOR_STATS: VendorStats = {
  shopVisits: EMPTY_BLOCK,
  contentViews: EMPTY_BLOCK,
  orders: EMPTY_BLOCK,
  revenue: EMPTY_BLOCK,
  clicks: 0,
  annonces: 0,
};

/** Libellés d'affichage des périodes. */
export const PERIOD_LABELS: Record<StatsPeriod, string> = {
  '7d': '7 jours',
  '30d': '30 jours',
  month: 'Ce mois',
};

/**
 * Calcule la plage de la période choisie et la plage précédente
 * équivalente (pour la comparaison / tendance).
 */
export function getPeriodRange(period: StatsPeriod, now: Date = new Date()): PeriodRange {
  const to = new Date(now);
  if (period === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const prevFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    return { from, to, prevFrom, prevTo: from };
  }
  const days = period === '7d' ? 7 : 30;
  const from = new Date(now.getTime() - days * 24 * 3600 * 1000);
  const prevFrom = new Date(now.getTime() - 2 * days * 24 * 3600 * 1000);
  return { from, to, prevFrom, prevTo: from };
}

/** Compte les lignes d'une table sur une plage de dates (colonne `date`). */
async function countWithRange(
  table: string,
  filterCol: string,
  filterVal: number | number[],
  from: Date,
  to: Date,
): Promise<number> {
  try {
    let q = supabase
      .from(table)
      .select('id', { count: 'exact', head: true })
      .gte('date', from.toISOString())
      .lte('date', to.toISOString());
    q = Array.isArray(filterVal) ? q.in(filterCol, filterVal) : q.eq(filterCol, filterVal);
    const { count } = await q;
    return count || 0;
  } catch {
    return 0;
  }
}

export async function fetchVendorStats(
  vendorId: number | null | undefined,
  annonceIds: number[],
  period: StatsPeriod = '30d',
): Promise<VendorStats> {
  if (!vendorId && annonceIds.length === 0) return EMPTY_VENDOR_STATS;
  const range = getPeriodRange(period);
  const ids = [...new Set(annonceIds.filter((n) => Number.isFinite(n) && n > 0))];

  const stats: VendorStats = { ...EMPTY_VENDOR_STATS };

  try {
    // ── Visites de la vitrine ──
    if (vendorId) {
      const [cur, prev] = await Promise.all([
        countWithRange('visiteboutique', 'boutique_id', vendorId, range.from, range.to),
        countWithRange('visiteboutique', 'boutique_id', vendorId, range.prevFrom, range.prevTo),
      ]);
      stats.shopVisits = { value: cur, previous: prev };
    }

    // ── Vues des annonces (produits + services) ──
    if (ids.length > 0) {
      const [prodCur, prodPrev, servCur, servPrev] = await Promise.all([
        countWithRange('visiteproduit', 'id_produit', ids, range.from, range.to),
        countWithRange('visiteproduit', 'id_produit', ids, range.prevFrom, range.prevTo),
        countWithRange('visiteservicesiteweb', 'id_produit', ids, range.from, range.to),
        countWithRange('visiteservicesiteweb', 'id_produit', ids, range.prevFrom, range.prevTo),
      ]);
      stats.contentViews = { value: prodCur + servCur, previous: prodPrev + servPrev };
    }

    // ── Commandes + revenus ──
    if (vendorId) {
      try {
        const { data: commandes } = await supabase
          .from('commande')
          .select('totalttc, date_time')
          .eq('idvendeur', vendorId)
          .limit(3000);
        const rows = (commandes as { totalttc: string | null; date_time: string | null }[] | null) || [];
        let ordersCur = 0;
        let ordersPrev = 0;
        let revenueCur = 0;
        let revenuePrev = 0;
        rows.forEach((c) => {
          const d = c.date_time ? new Date(c.date_time) : null;
          if (!d || Number.isNaN(d.getTime())) return;
          const amount = parseFloat(String(c.totalttc ?? '0'));
          const safe = Number.isNaN(amount) ? 0 : amount;
          if (d >= range.from && d <= range.to) {
            ordersCur += 1;
            revenueCur += safe;
          } else if (d >= range.prevFrom && d < range.prevTo) {
            ordersPrev += 1;
            revenuePrev += safe;
          }
        });
        stats.orders = { value: ordersCur, previous: ordersPrev };
        stats.revenue = { value: revenueCur, previous: revenuePrev };
      } catch {
        /* ignore */
      }
    }

    // ── Clics / contacts + annonces publiées (cumuls) ──
    if (vendorId) {
      try {
        const { data: annonces } = await supabase
          .from('annonce')
          .select('click, publique')
          .eq('idvendeur', vendorId);
        const rows = (annonces as { click: string | null; publique: number | null }[] | null) || [];
        stats.clicks = rows.reduce((sum, a) => {
          const n = parseInt(String(a.click ?? '0'), 10);
          return sum + (Number.isNaN(n) ? 0 : n);
        }, 0);
        stats.annonces = rows.filter((a) => Number(a.publique) === 1).length;
      } catch {
        /* ignore */
      }
    }

    return stats;
  } catch {
    return stats;
  }
}

/** Récupère les identifiants d'annonces d'un vendeur (pour les stats de vues). */
export async function fetchVendorAnnonceIds(vendorId: number | null | undefined): Promise<number[]> {
  if (!vendorId) return [];
  try {
    const { data } = await supabase.from('annonce').select('id').eq('idvendeur', vendorId);
    return ((data as { id: number }[] | null) || []).map((a) => Number(a.id)).filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}